type ExternalEvent = { type: string; properties?: unknown }

const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const strings = (value: Record<string, unknown>, ...keys: string[]) =>
  keys.every((key) => typeof value[key] === "string" && value[key] !== "")

export type PartDeltaEvent = {
  type: "message.part.delta"
  properties: {
    sessionID?: string
    messageID: string
    partID: string
    /** `state.input.<name>` grows a tool's input while the model writes the call. */
    field: "text" | "state.output" | `state.input.${string}`
    delta: string
    byteOffset?: number
  }
}

export function parsePartDeltaEvent(event: ExternalEvent): PartDeltaEvent | undefined {
  if (event.type !== "message.part.delta" || !record(event.properties)) return
  if (!strings(event.properties, "messageID", "partID")) return
  const field = event.properties.field
  if (field !== "text" && field !== "state.output" &&
      !(typeof field === "string" && /^state\.input\.[A-Za-z][A-Za-z0-9_]*$/.test(field))) return
  if (typeof event.properties.delta !== "string") return
  if (event.properties.byteOffset !== undefined &&
      (!Number.isSafeInteger(event.properties.byteOffset) || (event.properties.byteOffset as number) < 0)) return
  if (event.properties.sessionID !== undefined && typeof event.properties.sessionID !== "string") return
  return event as PartDeltaEvent
}

export function hasSessionStatusPayload(event: ExternalEvent) {
  if (event.type !== "session.status" || !record(event.properties)) return false
  const status = event.properties.status
  return strings(event.properties, "sessionID") && record(status) &&
    (status.type === "idle" || status.type === "busy" || status.type === "retry" || status.type === "compacting")
}

export type SessionWaitingEvent = {
  type: "session.waiting"
  properties: { sessionID: string; launches: number }
}

export function parseSessionWaitingEvent(event: ExternalEvent): SessionWaitingEvent | undefined {
  if (event.type !== "session.waiting" || !record(event.properties)) return
  const launches = event.properties.launches
  if (!strings(event.properties, "sessionID") || !Number.isSafeInteger(launches) || (launches as number) < 0) return
  return event as SessionWaitingEvent
}
